<template>
  <div
    class="calling-dialog-root
      phone:m-0! phone:h-dvh! phone:w-screen! phone:max-h-dvh! phone:max-w-[100vw]! phone:overflow-hidden!"
  >
    <section
      v-if="alarm"
      class="w-60 rounded-4 bg-bg-base-bg2 phone:w-4/5 shadow-overlay"
    >
      <main class="px-8 py-6">
        <p class="mb-4 text-center text-1624 leading-8">
          {{ alarm.name }}
        </p>

        <div class="mx-auto mt-5 mb-9 flex h-20 w-20 items-center justify-center rounded-full bg-teal animate-border-pulse-teal">
          <wui-icon
            name="bellM"
            class="text-teal-tints-600"
            xlarge
          />
        </div>

        <p class="text-center text-2432">
          {{ alarm.time }}
        </p>

        <p class="mt-1 text-center text-1316 text-fg-base-fg22">
          {{ alarmDateDisplay }}
        </p>
      </main>

      <footer class="flex gap-x-2 border-t border-bor-15 p-2">
        <wui-btn
          class="h-10! grow"
          icon
          prepend-icon="refreshM"
          @click="onCloseDialog"
        >
          <span class="sr-only select-none">{{ $t('common.snooze') }}</span>
        </wui-btn>

        <terminate-button :session-id="rtcSessionFacade.session.id">
          <template #default="{ terminate, loading }">
            <wui-btn
              data-test="cancel-button"
              :disabled="loading"
              variant="negative"
              state="filled"
              icon
              class="grow"
              prepend-icon="stop"
              @click="terminate"
            >
              <span class="sr-only select-none">{{ $t('common.close') }}</span>
            </wui-btn>
          </template>
        </terminate-button>
      </footer>
    </section>

    <section
      v-else
      class="calling-dialog relative flex w-90 flex-col items-center rounded-24 bg-bg-base-bg2 shadow-overlay
        phone:h-full phone:min-h-full phone:w-full phone:max-w-none phone:rounded-none phone:shadow-none"
    >
      <div class="absolute top-4 right-4 z-10 phone:hidden">
        <wui-btn
          icon
          :size="32"
          prepend-icon="closeRegularMdM"
          variant="neut"
          state="alpha"
          @click="onCloseDialog()"
        >
          <span class="sr-only select-none">{{ $t('common.hideNotification') }}</span>
        </wui-btn>
      </div>

      <div
        class="flex w-full flex-col items-center px-5 pt-10 pb-5
          phone:grow phone:justify-center phone:pt-16 phone:pb-8"
      >
        <div class="calling-dialog-avatar mb-4">
          <contact-avatar
            :name="displayName"
            :is-room="!!room?.isGroup"
            :room="room?.isGroup ? room : undefined"
            :contact="!room?.isGroup ? contact : undefined"
            :size="96"
          />
        </div>
        <p
          class="text-heading-sb-18-24 text-fg-base-fg3 line-clamp-2 max-w-full text-center phone:text-2432"
          :title="displayName || number || ''"
        >
          {{ displayName || number }}
        </p>
        <p
          v-if="state === STATE.RINGING"
          class="mt-1 text-body-r-14-20 text-fg-base-fg10 text-center"
        >
          {{ $t('common.incomingCall') }}
        </p>
        <p
          v-else-if="state === STATE.PROGRESS"
          class="mt-1 text-body-r-14-20 text-fg-base-fg10 text-center"
        >
          {{ $t('common.outgoingCall') }}
        </p>
        <p
          v-if="state === STATE.RINGING && invitedByName"
          class="mt-1 text-body-r-14-20 text-fg-base-fg22 truncate max-w-full text-center"
        >
          {{ $t('common.callInvitedBy', { name: invitedByName }) }}
        </p>
      </div>

      <footer
        class="flex w-full shrink-0 gap-3 px-5 pb-7 pt-2
          phone:px-6 phone:pb-10"
      >
        <template v-if="state === STATE.RINGING">
          <incoming-answer-actions
            :session-id="rtcSessionFacade.session.id"
            :with-audio="audio"
            :with-video="false"
            hide-answer-menu
            :size="72"
            button-state="filled"
            accept-icon="phoneF"
            button-class="call-modal-action-btn h-15! grow rounded-16! p-0!"
            :sr-only-label="$t('common.acceptCall')"
          />
          <terminate-button :session-id="rtcSessionFacade.session.id">
            <template #default="{ terminate, loading }">
              <wui-btn
                data-test="cancel-button"
                :disabled="loading"
                icon
                variant="negative"
                :size="72"
                state="filled"
                class="call-modal-action-btn h-15! grow rounded-16! p-0!"
                :prepend-icon="loading ? 'spinner' : 'phoneDownF'"
                @click="terminate"
              >
                <span class="sr-only select-none">{{ $t('common.cancelCall') }}</span>
              </wui-btn>
            </template>
          </terminate-button>
        </template>
        <terminate-button
          v-else
          :session-id="rtcSessionFacade.session.id"
        >
          <template #default="{ terminate, loading }">
            <wui-btn
              data-test="cancel-button"
              :disabled="loading"
              variant="negative"
              state="filled"
              class="h-15! w-full rounded-16! text-heading-m-16-22"
              auto-loading
              @click="terminate"
            >
              {{ $t('common.end') }}
            </wui-btn>
          </template>
        </terminate-button>
      </footer>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { closeDialogKey, safeInject, useRenderNotifier, WuiBtn, WuiIcon } from '@wui/common-library'
import dayjs from 'dayjs'
import { storeToRefs } from 'pinia'
import { onUnmounted, toRef, ref, computed, triggerRef, onMounted, watch } from 'vue'


import { IncomingAnswerActions, TerminateButton } from '@/features/incoming-call-buttons'

import { AlarmSchedule, useStoreAlarm } from '@/entities/alarm'
import { getInitialAudioEnabledOnJoin, useCallSessionRoomStore } from '@/entities/call-session'
import { useCallRingingStore , useIncomingCallSpeaker, createIncomingCallSpeaker, useCallsStore } from '@/entities/calls'
import { useStoreContacts } from '@/entities/contact'
import { ContactAvatar } from '@/entities/contact-avatar'
import { usePreferencesStore, PreferenceKey } from '@/entities/preference'
import { useStoreRooms } from '@/entities/room'
import { useNotification } from '@/entities/use-notification'
import { RTCSessionFacade, STATE, useWebRTC, NO_ANSWER_TIMEOUT } from '@/entities/webrtc'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { Contact } from '@/shared/types/api'
import { getSessionInvitedByName } from '@/shared/utils/jssip'
import { pServedIsRoom } from '@/shared/utils/uri'

const props = defineProps<{
  rtcSessionFacade: RTCSessionFacade
}>()

const session = toRef(props, 'rtcSessionFacade')
const sessionRoomStore = useCallSessionRoomStore()
const room = computed(() => sessionRoomStore.getRoom(session.value.sessionId))

const { currentUser } = useCurrentUser()
const { preferences } = usePreferencesStore()

const audio = ref(getInitialAudioEnabledOnJoin(room.value, {
  micDisabledOnStartConferenceGroup: !!preferences[PreferenceKey.MIC_DISABLED_ON_START_CONFERENCE_GROUP],
  pushToTalkGroup: !!preferences[PreferenceKey.PUSH_TO_TALK_GROUP],
  currentUserImLogin: currentUser.value?.imLogin,
}))

const { awaitForRoomAdded } = useStoreRooms()
const { createNewIncomingCallNotification, closeNewIncomingCallNotification } = useNotification()
const { notifyError } = useRenderNotifier()
const { sessions } = useWebRTC()
const { t } = useLocalization()
const { isSessionRingingEnabled, muteBySessionId } = useCallRingingStore()
const closeModal = safeInject(closeDialogKey)
const { play, stop, paused } = useIncomingCallSpeaker(createIncomingCallSpeaker())
const { callMediaRequests } = storeToRefs(useCallsStore())

const { findByPServed } = useStoreContacts()

const state = computed(() => props.rtcSessionFacade.sessionState.value)
const displayName = computed(() => session.value.displayName.value === 'Anonymous' ? t('common.numberIsHidden') : session.value.displayName.value)
const invitedByName = computed(() => getSessionInvitedByName(session.value.sessionId))
const number = computed(() => session.value.number)
const contact = computed<Contact | undefined>(() => room.value ? findByPServed(room.value.pServedUser) : undefined)
const isGroup = computed(() => pServedIsRoom(room.value?.pServedUser ?? ''))
const isRinging = computed(() => isSessionRingingEnabled(session.value.sessionId))
const mediaRequest = computed(() => callMediaRequests.value.get(props.rtcSessionFacade.callId.value))
/** Есть другой активный вызов — рингтон не играем (в т.ч. вне UI звонка) */
const hasOtherConnectedSession = computed(() => (
  Array.from(sessions.value.values()).some((facade) => (
    facade.sessionId !== session.value.sessionId
    && facade.sessionState.value === STATE.CONNECTED
  ))
))
const shouldPlayRingtone = computed(() => (
  isRinging.value
  && state.value === STATE.RINGING
  && !hasOtherConnectedSession.value
))
if (session.value.number && isGroup.value) {
  awaitForRoomAdded(session.value.number)
    .then((linkedRoom) => {
      useCallSessionRoomStore().linkSessionToRoom(session.value.sessionId, linkedRoom)
      session.value.displayName.value = linkedRoom.name
      session.value.number = linkedRoom.number
      triggerRef(sessions)
    }).catch((e: unknown) => {
      const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
      notifyError(errorMessage)
      session.value.terminate()
    })
}

const onCloseDialog = () => {
  muteBySessionId(session.value.sessionId)
  closeModal(undefined)
}

onMounted(() => {
  if (shouldPlayRingtone.value) {
    try {
      play()
    } catch (e) {
      console.error(e)
    }
  }
  if (state.value === STATE.RINGING) {
    createNewIncomingCallNotification(displayName.value, session.value.sessionId, isGroup.value)
  }

  if (mediaRequest.value?.requestedAudio === false) {
    audio.value = false
  }
})

onUnmounted(() => {
  closeNewIncomingCallNotification(session.value.sessionId)
  stop()
})

watch(shouldPlayRingtone, (shouldPlay) => {
  if (shouldPlay) {
    play()
    setTimeout(() => {
      if (!paused()) {
        stop()
      }
    }, NO_ANSWER_TIMEOUT)
    return
  }
  stop()
})

const { alarmSchedules } = storeToRefs(useStoreAlarm())
const now = dayjs().tz('Europe/Moscow').set('seconds', 0)
const alarm = computed<AlarmSchedule | null>(() => {
  if (session.value.number !== currentUser.value?.internalNumber) {
    return null
  }

  const al = Array.from(alarmSchedules.value.values()).find((item) => {
    if (!item.enabled) {
      return false
    }
    if (item.date) {
      const dt = dayjs(`${item.date} ${item.time}`)
      return dt.isSame(now, 'day') && dt.isSame(now, 'hour') && dt.isSame(now, 'minute')
    }

    return [[item.sunday, 0], [item.monday, 1], [item.tuesday, 2], [item.wednesday, 3], [item.thursday, 4], [item.friday, 5], [item.saturday, 6]]
      .filter(([val]) => !!val)
      .map((val) => val[1])
      .includes(now.day())
  })

  return al ?? null
})

const alarmDateDisplay = computed(() => {
  return alarm.value?.date ? dayjs(alarm.value.date).format(t('common.dateFormat')) : now.format(t('common.dateFormat'))
})
</script>

<style>
.call-modal-action-btn.wui-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
}

.call-modal-action-btn .wui-btn__row {
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 0 !important;
}

.call-modal-action-btn .wui-btn__icon--72,
.call-modal-action-btn svg {
  height: 32px !important;
  width: 32px !important;
}

.calling-dialog-avatar {
  height: 100px;
  width: 100px;
}

.calling-dialog-avatar .wui-avatar {
  height: 100px !important;
  width: 100px !important;
  min-height: 100px !important;
  min-width: 100px !important;
}

@media (max-width: 501px) {
  .wui-modal-overlay:has(.calling-dialog-root) {
    align-items: stretch;
    justify-content: stretch;
    padding: 0;
  }

  .wui-dialog__panel:has(.calling-dialog-root) {
    display: flex;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    margin: 0 !important;
    flex: 1 1 auto;
  }

  .calling-dialog-root {
    display: flex;
    flex: 1 1 auto;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    margin: 0 !important;
    overflow: hidden !important;
  }

  .calling-dialog-root > .calling-dialog {
    flex: 1 1 auto;
    width: 100% !important;
    max-width: none !important;
  }
}
</style>
